import { Component, EventListener, startComponents, stopComponents, triggerPublicEvent } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import addComponentAnimation from '@swisslife/shared-ui/src/utils/animations/js/animations';
import {
	IGOverlayHandlerOptions,
	IGScrollToTargetHandlerOptions,
} from '@swisslife/shared-types/src/types/gondel-public-events';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import 'formdata-polyfill';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
import { IValidationApi, IErrorTemplate } from '@swisslife/patterns-base/src/organisms/form/js/initParsley';

enum States {
	LoaderVisible = 'state-a-loader--visible',
	Error = 'state-m-contact-info__form--error',
}

enum Selectors {
	OverlayButton = '.js-m-contact-info__trigger-overlay',
	Section = '.js-m-contact-info__section',
	ContentWrapper = '.js-m-contact-info__form-content-wrapper',
	SuccessOutlet = '.js-m-contact-info__form-outlet',
	Loader = '.js-m-contact-info__form-loader',
	LocatorForm = '.js-m-contact-info__form',
	FormElement = '.js-m-form-element',
}

enum ServerSideValidationStates {
	Failed = 'validation_error',
	Passed = 'validation_ok',
}

interface IValidationFailedEntries {
	[field: string]: string[];
}

interface IValidationInfo {
	failedValidationEntries: IValidationFailedEntries;
	successValidationEntries: string[];
}

interface IValidationResult {
	result: ServerSideValidationStates;
	validationInfo: IValidationInfo;
}

interface IFieldServerSideErrorCallback {
	[fieldName: string]: ServerSideErrorCallbackFunction;
}

type ValidationInitCallbackFunction = () => any | never;
type ServerSideErrorCallbackFunction = (
	errorMessageArray: string[],
	errorTemplate: IErrorTemplate,
	isFirstError: boolean
) => any | never;

export type ValidateCallbackFunction = () => any | never;

@Component('ContactInfo')
export class ContactInfo extends GondelJqueryComponent {
	private controllers: any[] = Array();
	private submitAjax: boolean;
	private target: string;
	private validationUri: string;
	private submitUri: string;
	private isReInvokedNativeSubmit: boolean = false;
	private isFirstFormInteraction: boolean = true;
	private validationInstance: IValidationApi;
	private formValidationInitCallbacks: ValidationInitCallbackFunction[] = [];
	private fieldServerSideErrorCallbacks: IFieldServerSideErrorCallback = {};
	private $contentWrapper: JQuery<HTMLElement>;
	private $locatorForm: JQuery<HTMLElement>;
	private $successOutlet: JQuery<HTMLElement>;
	private $loader: JQuery<HTMLElement>;

	public async start() {
		this.controllers = await addComponentAnimation(this.$ctx, Selectors.Section);
		this.$locatorForm = $(Selectors.LocatorForm, this.$ctx);
		this.submitAjax = this.$locatorForm.data('submit-ajax') || false;
		this.target = this.$locatorForm.attr('target') || '';
		this.validationUri = this.$locatorForm.data('validation-uri') || '';
		this.submitUri = this.$locatorForm.attr('action') || '';
		this.$contentWrapper = $(Selectors.ContentWrapper, this.$locatorForm);
		this.$successOutlet = $(Selectors.SuccessOutlet, this.$locatorForm);
		this.$loader = $(Selectors.Loader, this.$locatorForm);
	}

	public async sync() {
		if (this.$locatorForm.length > 0) {
			this.initForm();
		}
	}

	public stop() {
		for (const controller of this.controllers) {
			controller.destroy(true);
		}
	}

	public addGroupValidation(selector: string, stateClass: string, callbackFn: ValidateCallbackFunction) {
		this.validationInstance.addGroupValidation(selector, stateClass, callbackFn);
	}

	public registerValidationInitCallback(fnCallback) {
		this.formValidationInitCallbacks.push(fnCallback);
	}

	public registerFieldServerSideErrorCallback(fieldName: string, callbackFn: ServerSideErrorCallbackFunction) {
		this.fieldServerSideErrorCallbacks[fieldName] = callbackFn;
	}

	public updateValidationAfterContentChange() {
		this.validationInstance.triggerRefresh();
	}

	public handleFormValidation = ($field: JQuery<HTMLElement>) => {
		if ($field.length > 0) {
			dispatchCustomEvent(this.$locatorForm, EventNames.FormValidationFailed);
			this.scrollToTarget($field.closest(Selectors.FormElement));
			$field.focus();
		}
	};

	@EventListener('gFormInteractionEvent')
	public _handleFormInteractionEvent() {
		if (this.isFirstFormInteraction) {
			dispatchCustomEvent(this.$locatorForm, EventNames.FormInteraction);
			this.isFirstFormInteraction = false;
		}
	}

	@EventListener('submit')
	public _handleFormSubmit(ev: Event) {
		this.toggleLoadingState(true);

		// do not interfere native submit for case submit via reload and already passed validation
		if (this.isReInvokedNativeSubmit) {
			return;
		}

		ev.preventDefault();
		this.submitForm();
	}

	@EventListener('click', Selectors.OverlayButton)
	private _handleOverlayButtonClick(ev: MouseEvent) {
		ev.preventDefault();

		const $target = $(ev.currentTarget!);

		const eventOptions: IGOverlayHandlerOptions = {
			overlayComponent: 'ContactInfo',
			overlayTitle: $target.data('overlay-title') || '',
			overlayContentUrl: $target.data('overlay-content-url') || '',
		};

		triggerPublicEvent('gOverlayHandler', this, undefined, eventOptions);
	}

	private async initForm() {
		// tslint:disable-next-line
		const asyncModule = await import(/* webpackChunkName: "dynamic-imports/parsley" */ '@swisslife/patterns-base/src/organisms/form/js/initParsley');

		this.validationInstance = asyncModule.initialize(this.$locatorForm);
		this.validationInstance.addValidatedCallback(this.handleFormValidation);

		for (const callback of this.formValidationInitCallbacks) {
			callback();
		}
	}

	private isFormInitialized = async (): Promise<boolean> => {
		if (this.validationUri === '') {
			return false;
		}
		return true;
	};

	private isFormValid = async (): Promise<boolean> => {
		const serializedData = this.getFormSerializedData();

		try {
			const validationResult = await ajax<IValidationResult>(this.validationUri, {
				method: 'POST',
				dataType: 'json',
				data: serializedData,
			});

			// Stop if any server validation failed
			if (validationResult.result === ServerSideValidationStates.Failed) {
				this.showServerSideValidationErrors(validationResult);

				return false;
			}
			return true;
		} catch (error) {
			this.$locatorForm.addClass(States.Error);
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$locatorForm, EventNames.NetworkError, { statusCode });
			return false;
		}
	};

	private submitForm = async (): Promise<void> => {
		if (!(await this.isFormInitialized())) {
			this.toggleLoadingState(false);
			return;
		}
		if (!(await this.isFormValid())) {
			this.toggleLoadingState(false);
			dispatchCustomEvent(this.$locatorForm, EventNames.FormValidationFailed);
			return;
		}

		if (this.submitAjax) {
			dispatchCustomEvent(this.$locatorForm, EventNames.FormSubmit, { submitType: 'ajax' });
			this.doAjaxSubmit();
		} else {
			// the else case happens for submit via page reload or custom action which submit via target _blank
			dispatchCustomEvent(this.$locatorForm, EventNames.FormSubmit, { submitType: 'reload' });

			// trigger form submit again and let the normal submit via page load happen
			this.isReInvokedNativeSubmit = true;
			this.$locatorForm.submit();

			// reset values again to be able to submit custom action form multiple times
			if (this.target === '_blank') {
				this.isReInvokedNativeSubmit = false;
				this.toggleLoadingState(false);
			}
		}
	};

	private showServerSideValidationErrors(validationResult: IValidationResult) {
		const validationErrors = validationResult.validationInfo.failedValidationEntries;
		let isFirstError = true;

		for (const property in validationErrors) {
			if (validationErrors.hasOwnProperty(property)) {
				const fieldName = property;
				const errorMessageArray = validationErrors[property];
				const $inputField = this.$locatorForm.find(`[name="${fieldName}"]`);

				if ($inputField.length > 0) {
					this.validationInstance.addErrors($inputField, errorMessageArray);
				}

				if ($inputField.length > 0 && isFirstError) {
					this.scrollToTarget($inputField);
				}

				// add possible field server side error callback
				if (this.fieldServerSideErrorCallbacks[fieldName] !== undefined) {
					this.fieldServerSideErrorCallbacks[fieldName](
						errorMessageArray,
						this.validationInstance.getErrorTemplate(),
						isFirstError
					);
				}

				if (isFirstError) {
					isFirstError = false;
				}
			}
		}
	}

	private async doAjaxSubmit() {
		if (this.submitUri === '') {
			return;
		}

		const formData = new FormData(this.$locatorForm.get(0) as HTMLFormElement);

		try {
			const submitResult = await ajax<string>(this.submitUri, {
				method: 'POST',
				type: 'text/json',
				dataType: 'html',
				contentType: false, // needed to work in combination with file-upload
				processData: false, // needed to work in combination with file-upload
				data: formData,
			});

			stopComponents(this.$contentWrapper);

			this.$contentWrapper.remove();
			this.$successOutlet.append(submitResult);

			startComponents(this.$successOutlet);

			this.toggleLoadingState(false);
			this.scrollToTarget(this.$successOutlet);
		} catch (error) {
			this.$locatorForm.addClass(States.Error);
			this.toggleLoadingState(false);
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$locatorForm, EventNames.NetworkError, { statusCode });
		}
	}

	private scrollToTarget($target: JQuery<HTMLElement>) {
		const eventOptions: IGScrollToTargetHandlerOptions = {
			target: $target,
			duration: 500,
		};

		triggerPublicEvent('gScrollToTargetHandler', this, undefined, eventOptions);
	}

	private toggleLoadingState(newState: boolean): void {
		if (this.$loader.length === 0) {
			return;
		}

		if (newState) {
			this.$loader.addClass(States.LoaderVisible);
		} else {
			this.$loader.removeClass(States.LoaderVisible);
		}
	}

	private getFormSerializedData(): string {
		const serializedFormData = this.$locatorForm.serialize();

		return serializedFormData;
	}
}
